import {
  Avatar,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { WSIcons } from "../../../commonFunctions/iIcons";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import WSImageUpload from "../../../Components/FileUpload/WSImageUpload";
import { useForm } from "react-hook-form";
import WSButton from "../../../Components/Button/WSButton";
import LiveLocationTracker from "../../../Components/MapView/MapView";
import SwipeableDrawerForm from "../../../Components/DrawyerForm/DrawyerForm";
import WSBackButton from "../../../Components/BackButton/BackButton";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import { LabAssignOrder } from "./LabAssignOrder";
import LabTechnicianDrawer from "./LabTechnicianDrawer";
import config from "../../../Config/config.json";
import { buildImageUrl } from "../../../commonFunctions/CommonFunctions";

interface TechnicianData {
  id: string;
  name: string;
  phoneNumber: string;
  fullAddress: string | null;
  dateOfBirth: string | null;
  qualification: string | null;
  experience: number;
  workingHours: string | null;
  profileImageUrl: string;
  isAadharVerified: boolean;
  isLicenseVerified: boolean;
  isCertificationVerified: boolean;
  isPoliceVerified: boolean;
  isActive: boolean;
  labId: string;
  lab: null;
  createdAt: string;
  updatedAt: string | null;
  documents: any[];
 
  profileName: string; // ✅ Use profileName from your sample
  contact: string;     // ✅ Use contact instead of phoneNumber
  
  profileImage: string;
}

interface RecentHistory {
  orderId: string;
  status: string;
  customer: string;
  customerAddress: string;
  orderTime: string;
  deliverytime: string;
}

interface MapLocation {
  latitude: string;
  longitude: string;
}

interface DashboardData {
  assignedTime: string | null;
  technician: TechnicianData | null; // ✅ Changed from technicianData
  todayCompletionPercentage: number;
  totalCompletionPercentage: number;
  mapLocation: MapLocation | null;
  ongoing: any | null;
  next: any[];
  recentHistory: RecentHistory[];
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: DashboardData ;
}

export const LabTechnicianDetails: React.FC<any> = React.memo((props) => {
  const { control, setValue, reset, register, handleSubmit } = useForm();
  const [open, setOpen] = React.useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = React.useState(false);
  const [formComponent, setFormComponent] = useState<any>();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);

  const params = useParams<{ id: string }>();
  const id = params?.id;


 const getImageUrl = useCallback((imagePath: string): string => {
  if (!imagePath || typeof imagePath !== 'string') return '';
  return buildImageUrl(imagePath, config.imgUrl); 
}, []);




  // ✅ ALL useEffect hooks FIRST - BEFORE any early returns
  const fetchTechnicianData = useCallback(async () => {
    if (!id) {
      console.error("❌ No technician ID");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response: ApiResponse = await callAPI(
        `lab/Technician/Technician/${id}/dashboard`,
        Method.GET
      );

      console.log("✅ API Response:", response);

      if (response.status && response.data?.technician) {
        // ✅ Use .technician
        setDashboardData(response.data);

        // ✅ Use correct field name
        const technician = response.data.technician; // NOT technicianData
        setValue("name", technician.name);
        setValue("phoneNumber", technician.phoneNumber);
      } else {
        console.warn("⚠️ Invalid API response:", response);
      }
    } catch (error) {
      console.error("💥 Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id, setValue]);

  // ✅ useEffect 1: Fetch data
  useEffect(() => {
    fetchTechnicianData();
  }, [fetchTechnicianData]);

  // ✅ useEffect 2: Breadcrumbs - FIXED syntax and moved BEFORE returns
  useEffect(() => {
    breadCrumbsStore.updateBreadCrumbs([
      { "Technician Management": "/lab-technician-manage/list" },
      { "Technician Details": `/lab-technician-details/${id || ""}` },
    ]);
  }, [id]);
  

  // ✅ SAFE LOADING CHECK - AFTER all hooks
  if (loading) {
    return (
      <>
        <WSBackButton id="btnScheduleBack" url="/lab-technician-manage/list" />
        <Paper style={{ padding: 20, marginTop: 30, borderRadius: "8px" }}>
          <Typography>Loading technician details...</Typography>
        </Paper>
      </>
    );
  }

  // Show partial UI if technician data exists (even if history/map is missing)
  if (loading || !dashboardData?.technician) {
    // ✅ Changed to .technician
    return (
      <>
        <WSBackButton id="btnScheduleBack" url="/lab-technician-manage/list" />
        <Paper style={{ padding: 20, marginTop: 30, borderRadius: "8px" }}>
          <Typography>Loading technician details...</Typography>
        </Paper>
      </>
    );
  }

  // 4. FIX safe extraction
  const technicianData = dashboardData!.technician!; // ✅ Now matches API
  const recentHistory = dashboardData.recentHistory || [];

  const columns: IColumns[] = [
    { header: "Order ID", fieldName: "orderId", type: "default" },
    { header: "Status", fieldName: "status", type: "status" },
    { header: "Customer", fieldName: "customer", type: "default" },
    {
      header: "Customer Address",
      fieldName: "customerAddress",
      type: "default",
    },
    { header: "Order Time & Date", fieldName: "orderTime", type: "date" },
    { header: "Delivery Time & Date", fieldName: "deliverytime", type: "date" },
  ];

  const handleOrders = () => {
    const form: any = (
      <LabAssignOrder
        id="assignOrderAdd"
        control={control}
        register={register}
        reset={reset}
        setValue={setValue}
        deliveryPersonId={id!}
        onCancel={() => {
          reset();
          setOpen(false);
          setFormComponent(null);
        }}
      />
    );
    setFormComponent(form);
    setTimeout(() => setOpen(true), 10);
  };
 const handleEditProfile = () => {
    if (dashboardData?.technician?.id) {
      setEditDrawerOpen(true);
    }
  };
  return (
    <>
      <WSBackButton id="btnScheduleBack" url="/lab-technician-manage/list" />

      {/* MAIN DASHBOARD PAPER */}
      <Paper
        elevation={0}
        style={{
          paddingLeft: 20,
          paddingRight: 20,
          marginTop: 30,
          paddingBottom: 25,
          borderRadius: "8px",
        }}
      >
       <Grid
    container
    justifyContent="space-between"
    alignItems="center"
    p={3}
  >
    <Grid item sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      {/* ✅ Profile image using EXACT ProductDetails method */}
      <Avatar
        src={getImageUrl(technicianData.profileImage)}
        alt={technicianData.profileName}
        sx={{ 
          width: 64, 
          height: 64,
          backgroundColor: "#ECF2FF"
        }}
      />

      <div>
        <Typography variant="h6" fontWeight={600}>
          {technicianData.profileName} {/* ✅ profileName from sample */}
        </Typography>
        <Stack direction="column" spacing={0.5} mt={1}>
          <Chip
            label={`+${technicianData.experience} Certified`}
            size="small"
            sx={{ backgroundColor: "#ECF2FF", color: "#2B52DE" }}
          />
          <Typography variant="body2" color="text.secondary">
            {technicianData.experience} Years Exp | {technicianData.id}
          </Typography>
        </Stack>
      </div>
    </Grid>

          <Chip
            label="Call"
            icon={<WSIcons.IoCall style={{ color: "#098963" }} />}
            size="small"
            sx={{
              ml: 2,
              color: "#098963",
              backgroundColor: "#CEEEE5",
              cursor: "pointer",
            }}
            onClick={() => window.open(`tel:${technicianData.phoneNumber}`)}
          />
        </Grid>
        <Divider />
        <Grid container spacing={2} p={3}>
          <Grid item xs={12}>
            <Typography>
              <WSIcons.IoLocationOutline style={{ color: "#022219" }} /> Live
              Location
            </Typography>
          </Grid>

          {recentHistory.length > 0 && (
            <>
              <Grid item xs={12} sm={1}>
                <Typography variant="body2" color="GrayText">
                  Order ID
                </Typography>
              </Grid>
              <Grid item xs={12} sm={11}>
                <Typography variant="body2" color="GrayText">
                  Z : #{recentHistory[0].orderId}
                </Typography>
                <Typography variant="body2" color="GrayText">
                  {recentHistory[0].customer}
                </Typography>
                <Typography variant="body2" color="GrayText">
                  {recentHistory[0].customerAddress}
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              📍 Current Location:{" "}
              {dashboardData.mapLocation
                ? `${dashboardData.mapLocation.latitude}, ${dashboardData.mapLocation.longitude}`
                : "Location unavailable"}
            </Typography>
            {dashboardData.mapLocation && (
              <LiveLocationTracker
                latitude={dashboardData.mapLocation.latitude}
                longitude={dashboardData.mapLocation.longitude}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography style={{ color: "#2B4CBA" }}>
              Assigned: {dashboardData.assignedTime || "Not assigned"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              sx={{ color: "#098963", borderRadius: 8 }}
              onClick={handleOrders}
            >
              <WSIcons.CiDeliveryTruck
                style={{ fontSize: 32, marginRight: 8 }}
              />
              {recentHistory.length}+ Orders{" "}
              <WSIcons.ArrowRight style={{ marginLeft: 8 }} />
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ color: "#022219", fontWeight: 500 }}>
              Performance overview
            </Typography>
          </Grid>
          <Grid container spacing={2} mt={3}>
            {[
              {
                label: "Completion Order (Today)",
                value: `${dashboardData.todayCompletionPercentage}%`,
              },
              {
                label: "Completion Order (Total)",
                value: `${dashboardData.totalCompletionPercentage}%`,
              },
              { label: "Avg Time", value: "28m" },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: 2,
                    backgroundColor: "#ECF2FF",
                  }}
                >
                  <Typography
                    fontSize={24}
                    fontWeight={400}
                    sx={{ color: "#022219" }}
                  >
                    {item.value}
                  </Typography>
                  <Typography color="text.secondary">{item.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography sx={{ color: "#022219", fontWeight: 500, mb: 5 }}>
              Recent History
            </Typography>
            <WSDataGridNew
              columns={columns}
              data={recentHistory}
              id="technicianListGrid"
              title=""
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* PERSONAL DETAILS PAPER */}
      <Paper
        elevation={0}
        style={{
          paddingLeft: 20,
          paddingRight: 20,
          marginTop: 20,
          paddingBottom: 25,
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={10}>
            <WSImageUpload
              control={control}
              id="image"
              name="image"
              label=""
              rules={{ required: false }}
              imgUrl={technicianData.profileImageUrl}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <WSButton
              id="edit"
              type="button"
              label="Edit"
              size="medium"
              variant="outlined"
              startIcon={<WSIcons.FaRegEdit />}
              color="primary"
              sx={{
                borderRadius: 8,
                width: "8rem",
                border: "1px solid #098963",
                color: "#098963",
              }}
              onClick={handleEditProfile}
            />
          </Grid>

          {/* PERSONAL DETAILS */}
          <Grid item xs={12}>
            <Typography sx={{ color: "#022219", fontWeight: 600, mb: 2 }}>
              Personal Details
            </Typography>
            <Grid
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #E0E0E0",
                backgroundColor: "#FAFAFA",
                mt: 3,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Phone Number
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="body2" color="text.primary">
                    : {technicianData.phoneNumber}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Date Of Birth
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="body2" color="text.primary">
                    : {technicianData.dateOfBirth 
       ? new Date(technicianData.dateOfBirth).toLocaleDateString('en-IN') 
       : "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="body2" color="text.primary">
                    : {technicianData.fullAddress || "No address"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Qualification
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="body2" color="text.primary">
                    : {technicianData.qualification || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* DOCUMENTS & VERIFICATIONS */}
          <Grid item xs={12}>
            <Typography sx={{ color: "#022219", fontWeight: 600, mb: 2 }}>
              Documents & Verifications
            </Typography>
            <Grid
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #E0E0E0",
                backgroundColor: "#FAFAFA",
                mt: 3,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Aadhar
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: technicianData.isAadharVerified
                        ? "#098963"
                        : "#FF6B6B",
                    }}
                  >
                    {technicianData.isAadharVerified ? "Verified" : "Pending"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    License
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: technicianData.isLicenseVerified
                        ? "#098963"
                        : "#FF6B6B",
                    }}
                  >
                    {technicianData.isLicenseVerified ? "Verified" : "Pending"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Certification
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: technicianData.isCertificationVerified
                        ? "#098963"
                        : "#FF6B6B",
                    }}
                  >
                    {technicianData.isCertificationVerified
                      ? "Verified"
                      : "Pending"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={10}>
                  <Typography variant="body2" color="GrayText">
                    Police Verification
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: technicianData.isPoliceVerified
                        ? "#098963"
                        : "#FF6B6B",
                    }}
                  >
                    {technicianData.isPoliceVerified ? "Verified" : "Pending"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* DRAWERS */}
        <SwipeableDrawerForm
          anchor="right"
          title="Assign Orders"
          openForm={open}
          formComponent={<form>{formComponent}</form>}
          onClose={() => {
            reset();
            setOpen(false);
            setFormComponent(null);
          }}
        />

        <SwipeableDrawerForm
          anchor="right"
          title="Edit Technician Profile"
          openForm={editDrawerOpen}
          id={`technicianEdit-${technicianData.id}`}
          isEdit={true}
          formComponent={
            <LabTechnicianDrawer
              key={`edit-drawer-${editDrawerOpen}-${technicianData.id}`}
              id={`technicianEdit-${technicianData.id}`}
              control={control}
              register={register}
              reset={reset}
              setValue={setValue}
              onCancel={() => {
                setEditDrawerOpen(false);
                reset({
                  name: "",
                  phoneNumber: "",
                  fullAddress: "",
                  dateOfBirth: "",
                  qualification: "",
                  experience: "",
                  workingHours: "",
                  "documents.0": "",
                  "documents.1": "",
                  "documents.2": "",
                  "documents.3": "",
                });
              }}
              onSuccess={() => {
                setEditDrawerOpen(false);
                fetchTechnicianData();
                window.location.reload(); // Removed - causes unnecessary full reload
              }}
            />
          }
          onClose={() => {
            setEditDrawerOpen(false);
            reset({
              name: "",
              phoneNumber: "",
              fullAddress: "",
              dateOfBirth: "",
              qualification: "",
              experience: "",
              workingHours: "",
              "documents.0": "",
              "documents.1": "",
              "documents.2": "",
              "documents.3": "",
            });
          }}
        />
      </Paper>
    </>
  );
});

export default LabTechnicianDetails;
